/**
 * The page-side API creatives and Ad Ops can call (friendly iframes reach it as
 * `window.top.hkejAds`; the legacy sites exposed `dismiss_skinner_Ad()` the same way):
 *
 *   window.hkejAds.dismissSkin()     remove a skin (wallpaper image or custom gutters) → full-width page
 *   window.hkejAds.dismissPopup()    close the light box / interstitial
 *   window.dismiss_skinner_Ad()      legacy name for dismissSkin (the old skin creatives call it)
 *
 * Installed by AdProvider on mount, ads enabled or not, so a page can always be asked.
 */

export interface HkejAdsApi {
  version: 1;
  dismissSkin(): void;
  dismissPopup(): void;
}

declare global {
  interface Window {
    hkejAds?: HkejAdsApi;
    dismiss_skinner_Ad?: () => void;
  }
}

export const SKIN_DISMISSED_EVENT = "hkejads:skin:dismissed";
export const POPUP_DISMISS_EVENT = "hkejads:popup:dismiss";

/** Nodes a custom creative attached to <body> for its overlay, by the `data-skin` value it used. */
function removeCreativeNodes(match: (v: string) => boolean) {
  document.querySelectorAll<HTMLElement>("[data-skin]:not([data-ad-host])").forEach((n) => {
    if (match(n.getAttribute("data-skin") ?? "")) n.remove();
  });
}

export function installPublicApi(): HkejAdsApi | undefined {
  if (typeof window === "undefined") return undefined;
  if (window.hkejAds?.version === 1) return window.hkejAds;
  const api: HkejAdsApi = {
    version: 1,
    dismissSkin() {
      // custom skin creatives mark their gutters with data-skin="<line item>Skinner" (or any value
      // that is not a light box / crazy overlay); the image host listens for the event
      removeCreativeNodes((v) => !/PopUp$|Crazy$/.test(v));
      window.dispatchEvent(new CustomEvent(SKIN_DISMISSED_EVENT));
    },
    dismissPopup() {
      removeCreativeNodes((v) => /PopUp$/.test(v));
      window.dispatchEvent(new CustomEvent(POPUP_DISMISS_EVENT));
    },
  };
  window.hkejAds = api;
  window.dismiss_skinner_Ad = () => api.dismissSkin();
  return api;
}
